import { ESdChangeReason, IAiAgentTemplate, IAiModel, IAiTemplate } from "@kuika/kuika-cl-model";
import * as React from "react";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import KuikaTemplateCard from "../../../ui-kit/kuika-template-card/kuika-template-card";
import AIActionTemplateDetailModal from "./ai-action-template-detail-modal";
import "./ai-action-template-detail-modal.scss";

export interface IAIActionTemplateState {
  isModalVisible: boolean;
}

export interface IAIActionTemplateProps {
  model?: IAiTemplate | IAiAgentTemplate;
  aiModels?: IAiModel[];
  selectedTemplateId?: string;
  setTemplateSelected: (modelId: string, name: string, template?: IAiTemplate | IAiAgentTemplate) => void;
  onEdit?: (templateId: string) => void;
  onCreateTemplate?: (modelId: string, name: string, template: IAiTemplate | IAiAgentTemplate) => void;
}

class AIActionTemplate extends React.PureComponent<IAIActionTemplateProps, IAIActionTemplateState> {
  constructor(props: IAIActionTemplateProps) {
    super(props);
    this.state = {
      isModalVisible: false
    };
  }

  handleCardClick = () => {
    const { model } = this.props;
    if (!model) {
      // For custom template, select it directly
      this.props.setTemplateSelected("custom", "Custom");
    } else {
      // For regular templates, show the modal
      this.setState({ isModalVisible: true });
    }
  };

  handleModalOk = () => {
    const { model } = this.props;
    if (model) {
      if (this.props.onCreateTemplate) {
        this.props.onCreateTemplate(model.id, model.name, model);
      } else {
        this.props.setTemplateSelected(model.id, model.name, model);
      }
    }
    this.setState({ isModalVisible: false });
  };

  handleSidebarEdit = () => {
    const { model } = this.props;
    if (!model) return;
    this.props.setTemplateSelected(model.id, model.name, model);
    this.setState({ isModalVisible: false });
    this.props.onEdit?.(model.id);
  };

  handleModalCancel = () => {
    this.setState({ isModalVisible: false });
  };

  handleCreateClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    const { model } = this.props;
    if (!model) {
      this.props.setTemplateSelected("custom", "Custom");
      return;
    }

    if (this.props.onCreateTemplate) {
      this.props.onCreateTemplate(model.id, model.name, model);
    } else {
      this.props.setTemplateSelected(model.id, model.name, model);
    }
  };

  handleViewDetailsClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    const { model } = this.props;
    if (!model) {
      this.props.setTemplateSelected("custom", "Custom");
      return;
    }

    this.setState({ isModalVisible: true });
  };

  private formatModelName = (name?: string): string => {
    if (!name) return "Unknown Model";

    // normalize separators
    const normalized = name.replace(/[-_]+/g, " ").trim();

    const tokens = normalized.split(/\s+/).map((token) => {
      // numeric tokens (versions etc.), leave as-is
      if (/^[0-9.]+$/.test(token)) return token;

      // small acronyms like gpt -> GPT
      if (/^[a-zA-Z]{1,3}$/.test(token)) return token.toUpperCase();

      // token with letters followed by digits, e.g. yolov8n -> Yolov8n or gpt4 -> GPT4
      const m = token.match(/^([a-zA-Z]+)(.*)$/);
      if (m) {
        const alpha = m[1];
        const rest = m[2];
        if (alpha.length <= 3) {
          return alpha.toUpperCase() + rest;
        }
        const titleAlpha = alpha[0].toUpperCase() + alpha.slice(1).toLowerCase();
        return titleAlpha + rest;
      }

      // fallback: Title Case
      return token[0].toUpperCase() + token.slice(1).toLowerCase();
    });

    return tokens.join(" ");
  };

  render() {
    const { model, aiModels } = this.props;

    if (!model) {
      return (
        <KuikaTemplateCard
          title="Custom"
          description="Create a custom AI action"
          selected={this.props.selectedTemplateId === "custom"}
          onClick={this.handleCardClick}
        />
      );
    }

    const modelInfo = aiModels?.find((m) => m.id === model.modelId);

    return (
      <>
        <KuikaTemplateCard
          title={KMainFunctions.formatCamelCase(model.name)}
          description={model.shortDescription || model.description}
          imageUrl={model.imageUrl}
          badge={modelInfo?.name ? this.formatModelName(modelInfo.name) : undefined}
          selected={model.id === this.props.selectedTemplateId}
          onClick={this.handleCardClick}
          primaryAction={{ label: "Use template", onClick: this.handleCreateClick }}
          secondaryAction={{ label: "Details", onClick: this.handleViewDetailsClick }}
        />

        <AIActionTemplateDetailModal
          visible={this.state.isModalVisible}
          template={model as IAiTemplate}
          modelName={this.formatModelName(modelInfo?.name)}
          isSelected={model.id === this.props.selectedTemplateId}
          isEditable={!!(model as any).editable}
          onOk={this.handleModalOk}
          onEdit={this.props.onEdit ? this.handleSidebarEdit : undefined}
          onCancel={this.handleModalCancel}
        />
      </>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    return false;
  };
}

export default AIActionTemplate;
